"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { usePathname } from "next/navigation";
import { Moon, Sun, Menu, X } from "lucide-react";
import { useTheme } from "next-themes";
import Link from "next/link";
import Image from "next/image";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const logoSrc = mounted && resolvedTheme === "light" 
    ? "/images/logo-light.png" 
    : "/images/logo-dark.png";

  const navLinks = [
    { name: "HOME", href: "/" },
    { name: "PORTFOLIO", href: "/projects" },
    { name: "ABOUT", href: "/about" },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 border-b border-border ${
        isScrolled 
          ? "bg-background/90 backdrop-blur-md py-3 shadow-lg" 
          : "bg-background/90 backdrop-blur-sm py-6 shadow-sm"
      }`}>
      <div className="px-6 md:px-12 max-w-7xl mx-auto flex justify-between items-center">
        <Link href="/" className="relative flex items-center">
          <Image 
            src={logoSrc} 
            alt="Folda Logo" 
            width={160} 
            height={44} 
            className={`transition-all duration-500 w-auto object-contain ${
              isScrolled ? "h-9" : "h-11"
            }`}
            priority
          />
        </Link>

        <div className="hidden md:flex space-x-8 items-center">
          {navLinks.map((link) => {
            const isActive = pathname === link.href;
            
            return (
              <Link
                key={link.name}
                href={link.href}
                className={`relative text-sm font-black tracking-widest transition-colors py-2 group ${
                  isActive 
                    ? "text-fd-500 opacity-100"
                    : "text-foreground/70 hover:text-fd-500/70 font-bold"
                  }`}
                >
                {link.name}

                {isActive && (
                  <motion.div
                    layoutId="nav-underline"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-fd-500 shadow-[0_0_8px_rgba(24,56,133,0.4)]"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}

                {!isActive && (
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-fd-500 transition-all duration-300 group-hover:w-full opacity-50" />
                )}
              </Link>
            );
          })}

          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="relative p-2.5 rounded-xl border border-border hover:border-fd-500 transition-all bg-secondary/30 text-foreground overflow-hidden group"
            aria-label="Toggle Theme"
            >
            <div className="absolute inset-0 bg-fd-500/0 group-hover:bg-fd-500/10 transition-colors duration-300" />
            
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={theme}
                initial={{ y: 15, opacity: 0, rotate: -45 }}
                animate={{ y: 0, opacity: 1, rotate: 0 }}
                exit={{ y: -15, opacity: 0, rotate: 45 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="relative z-10"
                >
                {mounted && (theme === "dark" ? (
                  <Sun size={18} className="text-fd-500" />
                ) : (
                  <Moon size={18} className="text-fd-500" />
                ))}
              </motion.div>
            </AnimatePresence>
          </button>

          <Link 
            href="/contact" 
            className="px-8 py-3 bg-fd-500 text-white dark:text-black dark:bg-fd-500 rounded-full font-black text-xs tracking-widest hover:brightness-110 transition-all transform hover:scale-105 shadow-xl shadow-fd-500/20 uppercase"
          >
            CONTACT
          </Link>
        </div>

        <div className="flex items-center space-x-4 md:hidden">
            <button
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="p-2 text-fd-500"
            >
                {mounted && (theme === "dark" ? <Sun size={20} /> : <Moon size={20} />)}
            </button>
            
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 text-foreground"
            >
                {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full left-0 right-0 md:hidden bg-background border-b border-border shadow-xl"
            >
              <div className="flex flex-col p-6 space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    href={link.href}
                    onClick={() => setIsOpen(false)}
                    className={`text-sm font-bold tracking-widest py-2 transition-colors ${
                      pathname === link.href 
                        ? "text-fd-500" 
                        : "text-muted-foreground hover:text-fd-500"
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                
                <div className="h-px bg-border w-full my-2" />

                <Link 
                  href="/contact"
                  onClick={() => setIsOpen(false)}
                  className="w-full py-4 bg-fd-500 text-white dark:text-black text-center rounded-xl font-black text-xs tracking-widest uppercase active:scale-[0.98] transition-transform"
                >
                  CONTACT
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </nav>
  );
}